import { createI18n } from 'vue-i18n'
import { SUPPORTED_LOCALES, DEFAULT_LOCALE, isSupportedLocale } from '@lark-automadoc/shared-types'
import type { SupportedLocale } from '@lark-automadoc/shared-types'
import { en, zh, id, es, vi, th, ja, LOCALE_META } from '@lark-automadoc/shared-ui/locales'

export type { SupportedLocale }

const STORAGE_KEY = 'automadoc-company-locale'

/**
 * Detect the best locale based on:
 * 1. localStorage preference (if set)
 * 2. Browser language
 * 3. Fallback to 'en'
 */
function detectLocale(): SupportedLocale {
  // Check localStorage first
  const stored = localStorage.getItem(STORAGE_KEY)
  if (stored && isSupportedLocale(stored)) {
    return stored
  }

  // Check browser language
  const browserLang = navigator.language.split('-')[0]
  if (isSupportedLocale(browserLang)) {
    return browserLang
  }

  // Fallback to default
  return DEFAULT_LOCALE
}

const initialLocale = detectLocale()
// The page's language from the first paint, not only after a switch: index.html says
// "en", and a Japanese or Chinese page marked "en" is drawn with the wrong CJK glyphs
// and read aloud in the wrong voice (the public signing page is often a first visit).
document.documentElement.lang = initialLocale

export const i18n = createI18n({
  locale: initialLocale,
  fallbackLocale: DEFAULT_LOCALE,
  legacy: false,
  messages: { en, zh, id, es, vi, th, ja }
})

/**
 * Change the current locale and persist to localStorage
 */
export function setLocale(locale: SupportedLocale): void {
  if (isSupportedLocale(locale)) {
    i18n.global.locale.value = locale
    localStorage.setItem(STORAGE_KEY, locale)
    // Update HTML lang attribute for accessibility
    document.documentElement.lang = locale
  }
}

/**
 * Get the current locale
 */
export function getLocale(): SupportedLocale {
  return i18n.global.locale.value as SupportedLocale
}

/**
 * Get all supported locales with their display names
 */
export function getSupportedLocales() {
  return SUPPORTED_LOCALES.map(code => ({
    code,
    name: LOCALE_META[code].name,
    flag: LOCALE_META[code].flag,
  }))
}
